<script setup>
/**
 * "Start from a goal" (spec P-4.5): five chips, one sentence, and a priced "Write it for me".
 *
 * It PROPOSES. The answer is handed to the page (`drafted`), which fills its own form with it
 * and runs its own rules; nothing here saves anything, and nothing here talks to WhatsApp. The
 * price is on the button, the owner confirms it in the shared dialog (P-5.12), and the receipt
 * says what was charged. Every failure arm on the server charges nothing, so every failure line
 * says so.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AiConfirmDialog from './AiConfirmDialog.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloI18n } from '../i18n.js';
import { wasloApi, withConfirm } from '../api/client.js';
import { TEMPLATE_GOALS } from '../helpers/template-draft.js';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /**
   * A goal to open with (spec P-8.2: a task drafted from a description suggests a message to go
   * with it). Only ever pre-fills: nothing is written until she presses Write it for me herself.
   */
  initialGoal: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
});

const emit = defineEmits(['drafted']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();

/** The server's cap on the sentence, after trimming (`DRAFT_DESCRIPTION_MAX`). */
const DESCRIPTION_MAX = 300;

/** One literal key per chip, so a key search and the translation checker see every one. */
const CHIP_LABEL = {
  appointment_reminder: () =>
    t('WASLO.TEMPLATES.EDITOR.GOAL.CHIPS.APPOINTMENT_REMINDER'),
  order_ready: () => t('WASLO.TEMPLATES.EDITOR.GOAL.CHIPS.ORDER_READY'),
  back_in_stock: () => t('WASLO.TEMPLATES.EDITOR.GOAL.CHIPS.BACK_IN_STOCK'),
  offer: () => t('WASLO.TEMPLATES.EDITOR.GOAL.CHIPS.OFFER'),
  custom: () => t('WASLO.TEMPLATES.EDITOR.GOAL.CHIPS.CUSTOM'),
};

const goal = ref(
  TEMPLATE_GOALS.includes(props.initialGoal) ? props.initialGoal : ''
);
const description = ref(props.initialDescription.slice(0, 300));
const isWriting = ref(false);
const failure = ref('');

const assist = useAiConfirm({ accountId, op: 'draft_message' });
const { receiptText } = assist;

const needsSentence = computed(
  () => goal.value === 'custom' && description.value.trim() === ''
);
const canWrite = computed(
  () =>
    goal.value !== '' &&
    !needsSentence.value &&
    !isWriting.value &&
    !props.disabled
);
const writeLabel = computed(() =>
  isWriting.value
    ? t('WASLO.TEMPLATES.EDITOR.GOAL.WRITING')
    : assist.label(t('WASLO.TEMPLATES.EDITOR.GOAL.WRITE'))
);

const choose = option => {
  goal.value = option;
  failure.value = '';
};

const write = async () => {
  if (!canWrite.value) return;
  isWriting.value = true;
  failure.value = '';
  try {
    const outcome = await assist.confirmAndRun({
      run: credits =>
        wasloApi.draftTemplate(
          accountId.value,
          { goal: goal.value, description: description.value.trim() },
          undefined,
          withConfirm(credits)
        ),
    });
    // Cancel costs nothing and changes nothing.
    if (outcome.cancelled) return;
    emit('drafted', outcome.result.draft);
  } catch (error) {
    // 422 is the server's `draft_unusable`: a draft with a price, a link or a number nobody gave
    // it. The sentence says so, because "try again" alone reads as a fault of hers.
    failure.value =
      error?.status === 422
        ? t('WASLO.TEMPLATES.EDITOR.GOAL.UNUSABLE')
        : t('WASLO.TEMPLATES.EDITOR.GOAL.FAILED');
  } finally {
    isWriting.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col gap-4" data-waslo-template-goal>
    <SettingsHeader
      :heading="t('WASLO.TEMPLATES.EDITOR.GOAL.HEADING')"
      :description="t('WASLO.TEMPLATES.EDITOR.GOAL.DESCRIPTION')"
    />
    <div
      class="flex flex-wrap gap-2"
      role="radiogroup"
      :aria-label="t('WASLO.TEMPLATES.EDITOR.GOAL.HEADING')"
    >
      <Button
        v-for="option in TEMPLATE_GOALS"
        :key="option"
        :label="CHIP_LABEL[option]()"
        size="sm"
        :variant="goal === option ? 'solid' : 'faded'"
        :color="goal === option ? 'blue' : 'slate'"
        role="radio"
        :aria-checked="goal === option"
        :disabled="disabled || isWriting"
        :data-waslo-template-goal-chip="option"
        @click="choose(option)"
      />
    </div>
    <WasloTextArea
      v-if="goal"
      :model-value="description"
      :label="t('WASLO.TEMPLATES.EDITOR.GOAL.SENTENCE_LABEL')"
      :hint="t('WASLO.TEMPLATES.EDITOR.GOAL.SENTENCE_HINT')"
      :max-length="DESCRIPTION_MAX"
      :message="
        needsSentence ? t('WASLO.TEMPLATES.EDITOR.GOAL.SENTENCE_NEEDED') : ''
      "
      message-type="info"
      :disabled="disabled || isWriting"
      data-waslo-template-goal-sentence
      @update:model-value="value => (description = value)"
    />
    <div v-if="goal" class="flex">
      <Button
        :label="writeLabel"
        icon="i-lucide-wand-2"
        size="sm"
        :is-loading="isWriting"
        :disabled="!canWrite"
        data-waslo-template-goal-write
        @click="write"
      />
    </div>
    <p
      v-if="receiptText"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-ai-receipt
    >
      {{ receiptText }}
    </p>
    <p
      v-if="failure"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
      data-waslo-template-goal-failure
    >
      {{ failure }}
    </p>
    <!--
      Mounted once a goal is chosen, which is always before the dialog can open. Chatwoot's
      Dialog teleports through the vuex store, and the editor's own specs mount the create form
      with no store installed; they never choose a goal, so they never mount this.
    -->
    <AiConfirmDialog v-if="goal" :controller="assist" />
  </section>
</template>
